import { Button, Loader, SearchField } from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";

import { useContext, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import { listCompanies, getCompany } from "graphql/queries";
import { deleteCompany } from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import { CompanyCreateForm, CompanyUpdateForm } from ".";
import { LayoutContext } from "layout/context/LayoutContext";
import ActionIcon from "components/actions/ActionIcon";
import ManagedCompanies from "./ManagedCompanies";

const Companies = () => {
  const { me } = useContext(LayoutContext);
  const [canCreate, canUpdate, canDelete] = usePermissions([
    "company:create",
    "company:update",
    "company:delete",
  ]);
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companyDetails, setCompanyDetails] = useState();
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    companyId: null,
    companyName: null,
    companyNit: null,
  });
  const [modalCompany, setModalCompany] = useState({
    visible: false,
    companyId: null,
  });
  const [modalManagedCompanies, setModalManagedCompanies] = useState({
    visible: false,
    companyId: null,
  });
  const { companyId } = useParams();
  const navigate = useNavigate();
  const columnsMapped = useMemo(
    () =>
      companies.map((item) => ({
        ...item,
        actions: (
          <>
            <ActionIcon
              name={
                companyId === item.id
                  ? strings.actions.hide
                  : strings.actions.show
              }
              icon={companyId === item.id ? "VisibilityOff" : "Visibility"}
              color="primary"
              onClick={() => {
                navigate(
                  companyId === item.id
                    ? "/companies"
                    : `/companies/${item.id}/details`
                );
              }}
            />
            {canUpdate && (
              <ActionIcon
                name={strings.actions.edit}
                icon="Edit"
                color="primary"
                onClick={() => openModal("update", item.id)}
              />
            )}
            {canUpdate && !me.companyID && (
              <ActionIcon
                name={strings.actions.managedCompanies}
                icon="Apartment"
                color="primary"
                onClick={() =>
                  setModalManagedCompanies({
                    visible: true,
                    companyId: item.id,
                    companyName: item.name,
                  })
                }
              />
            )}
            {canDelete && !me.companyID && (
              <ActionIcon
                name={strings.actions.delete}
                icon="Delete"
                color="red.60"
                onClick={() => {
                  setModalDetails({
                    visible: true,
                    companyId: item.id,
                    companyName: item.name,
                    companyNit: item.nit,
                  });
                }}
              />
            )}
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companies, companyId]
  );

  const onNext = () => {
    fetchCompanies(paginationTokens.nextToken);
  };
  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchCompanies(previous, false);
  };
  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchCompanies(undefined, undefined, keywordSplit);
  };
  const openModal = (action, id) =>
    setModalCompany({ visible: true, action, companyId: id });
  const onSuccessAction = () => {
    setModalCompany({ visible: false });
    fetchCompanies();
  };

  async function fetchCompanies(token, isNext = true, keywords) {
    let filterObj = {};
    try {
      setCompaniesLoading(true);

      if (me.companyID)
        filterObj = { ...filterObj, or: [{ id: { eq: me.companyID } }] };
      if (me.managedCompanies.length > 0)
        filterObj = {
          ...filterObj,
          or: [
            ...(filterObj?.or ?? []),
            ...me.managedCompanies.map((cp) => ({ id: { eq: cp } })),
          ],
        };

      const companiesData = await API.graphql(
        graphqlOperation(listCompanies, {
          limit: !keywords && !me.companyID ? 10 : undefined,
          nextToken: token,
          filter: keywords
            ? {
                and: keywords.map((key) => ({
                  or: [
                    {
                      nit: { contains: key },
                    },
                    {
                      name: { contains: key },
                    },
                    {
                      abbreviation: { contains: key },
                    },
                    {
                      address: { contains: key },
                    },
                    {
                      phone: { contains: key },
                    },
                  ],
                })),
                ...filterObj,
              }
            : { ...filterObj },
        })
      );
      const companiesItems = companiesData.data.listCompanies.items;
      setCompanies(companiesItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken: companiesData.data.listCompanies.nextToken,
        };
      });
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    } finally {
      setCompaniesLoading(false);
    }
  }
  async function fetchCompany(id) {
    try {
      const companyData = await API.graphql(
        graphqlOperation(getCompany, {
          id,
        })
      );
      const companyItem = companyData.data.getCompany;
      setCompanyDetails(companyItem);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    }
  }
  async function companyDelete(id) {
    try {
      const result = await API.graphql(
        graphqlOperation(deleteCompany, { input: { id } })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.deleteCompany.id;
      setCompanies(companies.filter((item) => item.id !== deletedId));
      setModalDetails({
        visible: false,
        companyId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  useEffect(() => {
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.companyID, me.managedCompanies]);

  useEffect(() => {
    if (companyId) {
      fetchCompany(companyId);
      navigate(`/companies/${companyId}/details`);
    } else {
      setCompanyDetails(null);
      navigate(`/companies`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const tableColumns = [
    { key: "nit", label: strings.table.headers.nit, _style: { width: "20%" } },
    {
      key: "name",
      label: strings.table.headers.name,
      _style: { width: "20%" },
    },
    {
      key: "abbreviation",
      label: strings.table.headers.abbreviation,
      _style: { width: "10%" },
    },
    {
      key: "address",
      label: strings.table.headers.address,
      _style: { width: "20%" },
    },
    {
      key: "phone",
      label: strings.table.headers.phone,
      _style: { width: "10%" },
    },
    {
      key: "actions",
      label: "",
      _style: { width: "20%" },
    },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      {canCreate && (
        <Button
          style={styles.buttonAdd}
          children={strings.actions.addCompany}
          variation="primary"
          onClick={() => openModal("create")}
        />
      )}
      <div className="p-3">
        {(!me.companyID || me.managedCompanies.length > 0) && (
          <div style={styles.searchByKeyword}>
            <SearchField
              label={strings.table.search.label}
              placeholder={strings.table.search.placeholder}
              onSubmit={onKeywordSubmit}
              onClear={onKeywordSubmit}
            />
          </div>
        )}
        {companiesLoading ? (
          <Loader />
        ) : (
          <>
            <CTable
              className="table-centered"
              items={columnsMapped}
              columns={tableColumns}
              tableHeadProps={{ color: "light" }}
              responsive
              hover
            />
            <Button
              size="small"
              disabled={paginationTokens.previous?.length <= 0}
              style={styles.buttonPrevious}
              onClick={onPrevious}
            >
              <FiArrowLeft /> {strings.table.pagination.buttonPrevious}
            </Button>
            <Button
              size="small"
              disabled={!paginationTokens.nextToken}
              style={styles.buttonNext}
              onClick={onNext}
            >
              <FiArrowRight /> {strings.table.pagination.buttonNext}
            </Button>
          </>
        )}
        <CCollapse visible={!!companyDetails?.id} style={styles.collapseCard}>
          <CCard key={companyDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "{companyDetails?.name}"
              </h4>
              <p className="text-muted">
                <b>{strings.details.companyDescription}:</b>{" "}
                {companyDetails?.description}
              </p>
              <p className="text-muted">
                <b>{strings.details.abbreviation}:</b>{" "}
                {companyDetails?.abbreviation}
              </p>
              <p className="text-muted">
                <b>{strings.details.address}:</b> {companyDetails?.address}
              </p>
              <p className="text-muted">
                <b>{strings.details.city}:</b> {companyDetails?.city}
              </p>
              <p className="text-muted">
                <b>{strings.details.country}:</b> {companyDetails?.country}
              </p>
              <p className="text-muted">
                <b>{strings.details.nit}:</b> {companyDetails?.nit}
              </p>
              <p className="text-muted">
                <b>{strings.details.phone}:</b> {companyDetails?.phone}
              </p>
              <p className="text-muted">
                <b>{strings.details.legalRepNameAndContact}:</b>{" "}
                {companyDetails?.legalRepName},{" "}
                {companyDetails?.legalRepContact}
              </p>
              <p className="text-muted">
                <b>{strings.details.otherContacts}:</b>{" "}
                {companyDetails?.otherContacts?.join(", ")}
              </p>
              <p className="text-muted">
                <b>{strings.details.createdAt}:</b>{" "}
                {moment(companyDetails?.createdAt).format(
                  "YYYY-MM-DD HH:mm:ss"
                )}
              </p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>
      <CModal
        alignment="center"
        visible={modalDetails.visible}
        onClose={() => setModalDetails({ visible: false, companyId: null })}
        backdrop="static"
      >
        <CModalBody>
          <b>
            {strings.formatString(strings.modal.confirmDeleteQuestion, {
              name: modalDetails.companyName,
              nit: modalDetails.companyNit,
            })}
          </b>
        </CModalBody>
        <CModalFooter>
          <Button
            size="small"
            onClick={() => setModalDetails({ visible: false, companyId: null })}
          >
            {strings.modal.cancelButton}
          </Button>
          <Button
            size="small"
            variation="primary"
            onClick={() => companyDelete(modalDetails.companyId)}
          >
            {strings.modal.confirmButton}
          </Button>
        </CModalFooter>
      </CModal>
      {modalCompany.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalCompany.visible}
          onClose={() => setModalCompany({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalCompany.action === "create"
                ? strings.modalCompany.addTitle
                : strings.modalCompany.updateTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody>
            {modalCompany.action === "create" ? (
              <CompanyCreateForm onSuccess={onSuccessAction} />
            ) : (
              <CompanyUpdateForm
                companyId={modalCompany.companyId}
                onSuccess={onSuccessAction}
              />
            )}
          </CModalBody>
        </CModal>
      )}
      {companyId && <hr />}
      <Outlet />
      {modalManagedCompanies.visible && (
        <ManagedCompanies
          companyID={modalManagedCompanies.companyId}
          companyName={modalManagedCompanies.companyName}
          visible={modalManagedCompanies.visible}
          onClose={() => setModalManagedCompanies({ visible: false })}
        />
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Empresas",
    successDeleted: "Empresa eliminada con éxito",
    actions: {
      addCompany: "Agregar Empresa",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      managedCompanies: "Empresas Administradas",
      delete: "Eliminar",
    },
    responses: {
      errorFetch: "Error al consultar empresas",
    },
    table: {
      headers: {
        name: "Nombre",
        nit: "Nit",
        abbreviation: "Abreviatura",
        address: "Dirección principal",
        phone: "Teléfono",
        actions: "Acciones",
      },
      pagination: {
        buttonPrevious: "Atrás",
        buttonNext: "Siguiente",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles de la empresa",
      companyDescription: "Descripción",
      legalRepNameAndContact: "Nombre representante legal y contacto",
      otherContacts: "Otros contactos",
      abbreviation: "Abreviatura",
      address: "Dirección principal",
      city: "Ciudad",
      country: "País",
      createdAt: "Fecha de creación",
      nit: "NIT",
      phone: "Teléfono",
    },
    modal: {
      confirmDeleteQuestion:
        "¿Estás seguro de eliminar la empresa {name} con Nit {nit}?",
      cancelButton: "Cancelar",
      confirmButton: "Confirmar",
    },
    modalCompany: {
      addTitle: "Agregar Empresa",
      updateTitle: "Actualizar Empresa",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  modal: { width: "250px" },
  collapseCard: { marginTop: "15px" },
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default Companies;
